import { Inject } from '@odinjs/odin';
import moment from 'moment';

import Controller, { OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { AutocompleteAPI, ButtonAPI, FetchAPI, FormAPI, InputAPI, MapperAPI, RadioGroupAPI, SelectableItem, TextAPI } from '@philips-internal/oswald-ui-components';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';

import { BladderIrrigationStartAliases, FragmentNames } from '../../../../../codegen';
import BaseController from '../../../../BaseController';
import timeService from '../../../../../services/time-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { Endpoint } from '../../../../../constants/endpoints';
import { AppearanceOfLiquidType, IrrigationOrWashingType, Keys, RegisterBladderIrrigationResponse } from '../bladder-irrigation-params';
import { YesNoType } from '../../../../../utils/enum-utils';
import MedicationService from '../../../services/medication-service';
import { RedirectUserToTimeline, ShowToast } from '../../../services/informations-default-items-service';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

@Controller({ fragment: FragmentNames.BLADDER_IRRIGATION_START })
export default class BladderIrrigationStartController extends BaseController {

  @Inject(BladderIrrigationStartAliases.INFORMATION_FORM)
  private formApi!: FormAPI;

  @Inject(BladderIrrigationStartAliases.IRRIGATION_OR_WASHING_RADIO_GROUP)
  private irrigationOrWashingRadioGroup!: RadioGroupAPI;

  @Inject(BladderIrrigationStartAliases.BUTTON_EXECUTE)
  private executeButtonApi!: ButtonAPI;

  @OnMount(BladderIrrigationStartAliases.DATE_HOUR_INPUT)
  handleMountDateHourInput({ target }: EventData<InputAPI>): void {
    timeService.getCurrentDateTime()
      .then(date => target.setValue(date))
      .catch(() => target.setValue(moment().format(ISO_FULL_DATE_FORMAT)));
  }

  @OnMount([BladderIrrigationStartAliases.TEXT_BLADDER_IRRIGATION_DATE, BladderIrrigationStartAliases.TEXT_BLADDER_IRRIGATION_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const data = TimelineContext.get() as TimeLineEventMedication;
    const value = target.getValue()
      .replace(':bladder-irrigation-information', data.description as string)
      .replace(':bladder-irrigation-date', DateFormatterUtil.timeStamp(String(data.start)));

    target.setValue(value);
  }

  @OnMount(BladderIrrigationStartAliases.IRRIGATION_OR_WASHING_RADIO_GROUP)
  handleonMountRadioGroup({ target }: EventData<RadioGroupAPI>): void {
    const irrigationRadioOption = { key: Keys.KEY1, label: 'text.irrigation', value: IrrigationOrWashingType.IRRIGATION };
    const washingRadioOption = { key: Keys.KEY2, label: 'text.washing', value: IrrigationOrWashingType.WASHING }
    const radioGroupOptions = [irrigationRadioOption, washingRadioOption] as SelectableItem[]
    target.setData(radioGroupOptions);
    target.setValue(irrigationRadioOption)
  }

  @OnMount(BladderIrrigationStartAliases.APPEARANCE_OF_LIQUID_FETCH)
  handleMountDeviceFetch({ target }: EventData<FetchAPI>): void {
    target.setEndpoint(Endpoint.EMAR_GET_APPEARANCE_OF_LIQUID);
  }

  @OnMount(BladderIrrigationStartAliases.APPEARANCE_OF_LIQUID_AUTOCOMPLETE)
  handleAppearanceOfLiquidMount({ target }: EventData<AutocompleteAPI>): void {
    target.setEntry('appearanceOfLiquidDescription');
    target.setPrimaryKey('appearanceOfLiquidDescription');
  }

  @OnMount(BladderIrrigationStartAliases.APPEARANCE_OF_LIQUID_MAPPER)
  handleMountDeviceMapper({ target }: EventData<MapperAPI>): void {
    target.setMap({ value: 'appearanceOfLiquidDescription' });
  }

  @OnValidate(BladderIrrigationStartAliases.INFORMATION_FORM)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.executeButtonApi.setEnabled(validationFormTwo.valid);
  }

  @OnClick(BladderIrrigationStartAliases.BUTTON_EXECUTE)
  async handleOnClickButonExecute(): Promise<void> {
    const isValid = await this.formApi.validate()
    if(!isValid) {
      return
    }

    const { encounterId , scheduleSequenceNumber} = this.historyService.getCurrentRouteParams();
    const currentRecord = this.formApi.getRecord();
    const registerDate = moment(String(currentRecord.currentTime)).format(ISO_FULL_DATE_FORMAT);
    const drainedQuantity = currentRecord.quantityDrained ?? 0;
    const infusionQuantity = currentRecord.quantityInfusion ?? 0;
    const irrigationOrWashing = this.irrigationOrWashingRadioGroup.getValue() as SelectableItem;
    const observation = String(currentRecord.observation ?? '');
    const appearanceOfLiquid = currentRecord.appearanceOfLiquid ?? '';
    const aspectSequence = appearanceOfLiquid as AppearanceOfLiquidType;
    const irrigationSequence = MedicineAndSolutionsParams.params.nrSequenceId

    const body = {
      aspectSequence: isNaN(Number(aspectSequence.appearanceOfLiquidCode)) ? '' : aspectSequence.appearanceOfLiquidCode,
      drainedQuantity,
      encounterId: Number(encounterId),
      infusionQuantity,
      irrigationOrWashing: irrigationOrWashing.value,
      newIrrigation: YesNoType.YES,
      observation,
      registerDate,
      scheduleTimeSequence: Number(scheduleSequenceNumber)
    }

    HttpService.post(Endpoint.EMAR_POST_REGISTER_BLADDER_IRRIGATION, { irrigationSequence }, { body })
      .then((res) => {
        const responseBody = res.body as RegisterBladderIrrigationResponse;
        if (responseBody.irrigationSequence === null) {
          ShowToast(String(responseBody), ToastLevel.WARNING, ToastType.BANNER, 'bladder-irrigation-start-toast');
          return Promise.reject(false);
        }
        return Promise.resolve();
      }).then(() => {
        ShowToast('text.bladder-irrigation-started', ToastLevel.SUCCESS, ToastType.BANNER, 'bladder-irrigation-start-toast');
        this.isLoading(false);
        RedirectUserToTimeline([FragmentNames.BLADDER_IRRIGATION_START]);
      }).catch((error) => {
        const errorMessage = error.body.errors[0];
        MedicationService.showToast(errorMessage, ToastLevel.ERROR);
      });
  }

  private isLoading(state: boolean): void {
    this.executeButtonApi.setEnabled(!state);
    this.formApi.setLoading(state);
  }
}